import type { Employee } from './api.ts';
import { weekdayIndex } from './date-field.ts';

/**
 * Anzeige der Zeiterfassung (Ticket 1.284): Minuten vom Server als Text – nur Schreibweise, keine Rechnung. Gleiche Form
 * wie die Sätze des Servers (`formatDuration` in `staff`): „4:12 h“, Saldo mit Vorzeichen „+0:15 h“ / „−0:30 h“.
 */

/** Minuten als `4:12` (ohne Einheit); negative als Betrag. */
export function hoursMinutes(minutes: number): string {
  const m = Math.abs(Math.trunc(minutes));
  return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`;
}

/** Minuten als `4:12 h`. */
export function hoursText(minutes: number): string {
  return `${hoursMinutes(minutes)} h`;
}

/** Saldo mit Vorzeichen: `+0:15 h`, `−0:30 h` (echtes Minuszeichen), `0:00 h`. */
export function signedHoursText(minutes: number): string {
  const sign = minutes > 0 ? '+' : minutes < 0 ? '−' : '';
  return `${sign}${hoursText(minutes)}`;
}

/**
 * Anteil als ganze Prozent 0–100 (abgerundet, gedeckelt) für Ringe wie „Resturlaub … % übrig“; `total` ≤ 0 oder
 * ungültig → `null` (kein Ring). Nur Darstellung, Tage dürfen halbe sein.
 */
export function sharePercent(value: number, total: number): number | null {
  if (!Number.isFinite(value) || !Number.isFinite(total) || total <= 0) return null;
  return Math.min(100, Math.max(0, Math.floor((value * 100) / total)));
}

/** Uhrzeit `HH:MM` (Feld `from`/`to` des Servers, keine Satz-Auswertung) als Minute des Tages; ungültig → `null`. */
export function clockMinute(hhmm: string | null | undefined): number | null {
  const m = /^(\d{2}):(\d{2})$/.exec(hhmm ?? '');
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  return h < 24 && min < 60 ? h * 60 + min : null;
}

/** Minute des Tages als `HH:MM` (über 24 h ohne Folgetag-Angabe gekürzt auf den Rest). */
export function minuteClock(minute: number): string {
  const m = ((Math.trunc(minute) % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

/** Minute des Tages in Europe/Berlin eines Zeitpunkts (fachliches Jetzt der Oberfläche, nur Anzeige). */
export function berlinMinute(now: Date): number {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Berlin', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(now).map((p) => [p.type, p.value]),
  );
  return Number(parts.hour) * 60 + Number(parts.minute);
}

/** Initialen für den Avatar („Anna Test“ → „AT“, „Tobias (Test)“ → „TT“; nur Buchstaben und Ziffern, höchstens zwei). */
export function initials(name: string): string {
  const words = name
    .trim()
    .split(/\s+/)
    .map((w) => w.replace(/[^\p{L}\p{N}]/gu, ''))
    .filter(Boolean);
  const letters = words.length > 1 ? [words[0]![0], words.at(-1)![0]] : [words[0]?.[0], words[0]?.[1]];
  return letters.filter(Boolean).join('').toUpperCase();
}

/** Ton der Übertrag-Karte je Zustand (Story 22.9; Bernstein nur für die Vorwarnung) – Seite und Admin-Blatt. */
export const CARRYOVER_TONE: Readonly<Record<'open' | 'warning' | 'used' | 'expired', 'info' | 'check' | 'ready'>> = { open: 'info', warning: 'check', used: 'ready', expired: 'info' };

/** Anteil des Countdown-Rings: verbleibende Tage der Vorwarnfrist; am Stichtag (0 Tage) voller Ring. */
export function countdownShare(daysLeft: number, warningDays: number): number {
  if (daysLeft <= 0 || warningDays <= 0) return 1;
  return Math.min(1, daysLeft / warningDays);
}

/**
 * Grafik der Kachel „Diese Woche“ (Folgearbeit 26.10.2026): Säulen nur Mo–Fr und nur, wenn dort Ist-Zeit liegt (keine
 * flachen Säulen; Zeit nur am Wochenende → keine Säulen, die Fußzeile zeigt Soll/Ist). Eine Woche ganz ohne Zeit zeigt
 * keine Kachel (Ticket 22.27).
 */
export function weekTileGraphic<T extends { date: string; actualMinutes: number }>(days: readonly T[]): { workdays: T[]; columns: number[] | null } {
  const workdays = days.filter((d) => weekdayIndex(d.date) < 5);
  const columns = workdays.some((d) => d.actualMinutes !== 0) ? workdays.map((d) => d.actualMinutes) : null;
  return { workdays, columns };
}

/** Saldo als `+24:00`, `−4:00` (echtes Minuszeichen), `0:00` – ohne Einheit (Monatssummen, Story 22.13). */
export function signedHoursMinutes(minutes: number): string {
  const sign = minutes > 0 ? '+' : minutes < 0 ? '−' : '';
  return `${sign}${hoursMinutes(minutes)}`;
}

/** Tage von `from` bis `to` (ISO-Daten `YYYY-MM-DD`, Kalendertage; negativ, wenn `to` davor liegt). */
export function daysBetween(from: string, to: string): number {
  const utc = (iso: string) => Date.UTC(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10)));
  return Math.round((utc(to) - utc(from)) / 86_400_000);
}

/** Ganze Kalendermonate von `from` bis `to` (angebrochener Monat zählt nicht). */
function wholeMonths(from: string, to: string): number {
  const months = (Number(to.slice(0, 4)) - Number(from.slice(0, 4))) * 12 + (Number(to.slice(5, 7)) - Number(from.slice(5, 7)));
  return Number(to.slice(8, 10)) < Number(from.slice(8, 10)) ? months - 1 : months;
}

/**
 * Countdown bis zu einem Tag (Story 22.13, Hinweis „Nächste Anonymisierung“): „heute“, „morgen“, „noch 12 Tage“ (bis 60
 * Tage), „noch 5 Monate“ (bis 2 Jahre), sonst „noch 7 Jahre“ bzw. „noch 6 Jahre, 2 Monate“. Nur Anzeige.
 */
export function countdownText(today: string, target: string): string {
  const days = daysBetween(today, target);
  if (days <= 0) return 'heute';
  if (days === 1) return 'morgen';
  if (days <= 60) return `noch ${days} Tage`;
  const months = wholeMonths(today, target);
  if (months < 24) return `noch ${months} ${months === 1 ? 'Monat' : 'Monate'}`;
  const years = Math.floor(months / 12);
  const rest = months % 12;
  return rest === 0 ? `noch ${years} Jahre` : `noch ${years} Jahre, ${rest} ${rest === 1 ? 'Monat' : 'Monate'}`;
}

/**
 * Füllung der Zeitleiste „Austritt → Fristende → Anonymisierung“ je Abschnitt (0–1): erster Abschnitt nach verstrichenen
 * Tagen seit dem Austritt, zweiter (ein Tag) voll ab dem Tag der Anonymisierung. Nur Anzeige.
 */
export function retentionShares(exitDate: string, retentionEnd: string, anonymizeOn: string, today: string): [number, number] {
  const total = daysBetween(exitDate, retentionEnd);
  const first = total <= 0 ? 1 : Math.min(1, Math.max(0, daysBetween(exitDate, today) / total));
  return [first, today >= anonymizeOn ? 1 : 0];
}

/** Kopf-Zähler der Mitarbeiterliste (Story 22.13, Auflage 1): „aktiv · gesamt“ zählt alle gezeigten Zeilen, anonymisierte Personen stehen getrennt dabei. */
export function employeeSummary(items: readonly { status: string }[]): string | undefined {
  if (items.length === 0) return undefined;
  const active = items.filter((e) => e.status === 'active').length;
  const anonymized = items.filter((e) => e.status === 'anonymized').length;
  return `${active} aktiv · ${items.length} gesamt${anonymized > 0 ? ` · davon ${anonymized} anonymisiert` : ''}`;
}

/** Schon ausgetretene (Austritt vor `today`, Berlin), noch nicht anonymisierte Personen – nach Tag der Anonymisierung. */
export function pendingRetention(items: readonly Employee[], today: string): (Employee & { exitDate: string; retentionEnd: string; anonymizeOn: string })[] {
  return items
    .filter((e): e is Employee & { exitDate: string; retentionEnd: string; anonymizeOn: string } => e.status !== 'anonymized' && !!e.exitDate && e.exitDate < today && !!e.retentionEnd && !!e.anonymizeOn)
    .sort((a, b) => a.anonymizeOn.localeCompare(b.anonymizeOn) || a.displayName.localeCompare(b.displayName, 'de'));
}

/** Vorname, wenn er im ganzen Team eindeutig ist; sonst der ganze Name (kurze Pillen im Team-Kalender, Ticket 22.29). */
export function shortNames(people: readonly { employeeId: string; displayName: string }[]): Map<string, string> {
  const first = (n: string) => n.split(' ')[0] ?? n;
  const count = new Map<string, number>();
  for (const p of people) count.set(first(p.displayName), (count.get(first(p.displayName)) ?? 0) + 1);
  return new Map(people.map((p) => [p.employeeId, count.get(first(p.displayName)) === 1 ? first(p.displayName) : p.displayName]));
}
